import React, { useEffect, useState, useRef, Fragment } from "react";
import Input from "../Input";
import { fetchJson, post } from "../../lib/Fetching";
import _ from "underscore";
import { InputCheckedOutlineDimensions } from "@styled-icons/typicons/InputCheckedOutline";
import PrintAliquotBarcodes from "../sample_reception/PrintAliquotBarcodes";
import { playErrorSound, playSuccess, handleError } from "../../lib/StatusSounds";

function Queue(item_id) {
  this.reload = null;
  this.redraw = null;
  this.item_id = item_id;
  this.queue = [];
  this.add = (reference) => {
    const item = {
      reference,
      status: "pending",
      error: null,
      created_at: new Date(),
    };
    this.queue.push(item);
    this.redraw();
    this.makeRequest(item);
  };

  this.reloadView = () => {
    this.reload();
  };

  this.debounceReload = _.debounce(this.reloadView, 600);

  this.makeRequest = (item) => {
    post("/tracking/items/" + this.item_id + "/add.json", {
      barcode: item.reference,
    }).then((json) => {
      if (json.errors) {
        item.status = "error";
        item.error = json.errors;
        playErrorSound();
        this.redraw();
      } else {
        item.status = "completed";
        playSuccess();
        this.debounceReload();
      }
    });
  };
}

export default function Container({ item_id }) {
  const [item, setItem] = useState(null);
  const [barcode, setBarcode] = useState("");
  const queue = useRef(new Queue(item_id));
  const [showSampleTechniques, setShowSampleTechniques] = useState(false);
  const [showDueDates, setShowDueDates] = useState(false);
  const [showRequestedTestDate, setShowRequestedTestDate] = useState(false);
  const [showAllScans, setShowAllScans] = useState(false);
  const [showItemCounts, setShowItemCounts] = useState(false);
  const [sortByRequestedTestDate, setSortByRequestedTestDate] = useState(false);
  const [sortByDueDate, setSortByDueDate] = useState(false);
  const [sortByTurnaround, setSortByTurnaround] = useState(false);
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [aliquotPrinting, showAliquotPrinting] = useState(false);
  const [, updateState] = React.useState();
  const forceUpdate = React.useCallback(() => updateState({}), []);

  const fetchItem = () => {
    fetchJson("/tracking/items/" + item_id + ".json", {
      show_due_date: showDueDates,
      show_sample_techniques: showSampleTechniques,
      show_item_counts: showItemCounts,
      show_requested_test_date: showRequestedTestDate,
    }).then((item) => {
      setItem(item);
    });
  };
  queue.current.reload = fetchItem;
  queue.current.redraw = forceUpdate;

  useEffect(
    () => {
      fetchItem();
    },
    [showSampleTechniques, showDueDates, showItemCounts, showRequestedTestDate]
  );

  const addItem = () => {
    queue.current.add(barcode);
    setBarcode("");
  };

  const isSelected = (item) => {
    return selectedItemIds.includes(item.id);
  };

  const select = (item) => {
    if (selectedItemIds.includes(item.id)) {
      setSelectedItemIds(selectedItemIds.filter((id) => id !== item.id));
    } else {
      setSelectedItemIds(selectedItemIds.concat(item.id));
    }
  };

  const selectAll = () => {
    if (selectedItemIds.length === 0) {
      setSelectedItemIds(item.children.map((i) => i.id));
    } else {
      setSelectedItemIds([]);
    }
  };

  const removeSelectedItems = () => {
    if (selectedItemIds.length === 0) return;

    if (selectedItemIds.length > 1) {
      if (item.can_only_remove_one_item_at_a_time) {
        alert("You can only remove one item at a time from this container.");
        return;
      }
      if (
        !confirm(
          "WARNING: Are you sure you want to remove " +
            selectedItemIds.length +
            " items from this container?"
        )
      )
        return;
    }

    post(
      "/tracking/items/remove_items.json",
      {
        parent_item_id: item.id,
        ids: selectedItemIds,
      },
      "delete"
    )
      .then(() => {
        setSelectedItemIds([]);
        fetchItem();
      })
      .catch(handleError);
  };

  const displayQueue = showAllScans
    ? queue.current.queue
    : queue.current.queue.filter((item) => ["pending", "error"].includes(item.status));

  if (item == null) {
    return (
      <div className="basic-panel with-padding">
        <i className="fa fa-spinner fa-spin" />
        &nbsp;Loading...
      </div>
    );
  }

  const showNumber = (n) => {
    if (n === 0) return <span className="text-faint">0</span>;
    else return n;
  };

  var children = item.children;

  if (sortByRequestedTestDate || (showDueDates && (sortByDueDate || sortByTurnaround))) {
    children = _.sortBy(children, "name");
  }

  if (sortByRequestedTestDate) {
    children = _.sortBy(children, (c) => c.requested_test_date_i || 99999999999999);
  }
  if (showDueDates && sortByDueDate) {
    children = _.sortBy(children, (c) => c.due_date_i || 99999999999999);
  }
  if (showDueDates && sortByTurnaround) {
    children = _.sortBy(children, (c) => c.turnaround || 99999999999999);
  }

  return (
    <div className="panel">
      <div className="panel-body">
        <span className="pull-right text-muted">{item.children.length} Items</span>
        <h3 className="panel-title">
          <i className="fa fa-th" />
          &nbsp;&nbsp;Items contained within {item.name}
        </h3>
        <div className="row" style={{ marginTop: 20 }}>
          <div className="col-md-12">
            <div className="input-group">
              <Input
                focus={true}
                type="text"
                className="form-control"
                placeholder="Scan item / aliquot / sample barcode..."
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onPressEnter={addItem}
              />
              <div className="input-group-btn">
                <button className="btn btn-primary" onClick={addItem}>
                  <i className="fa fa-plus" />
                  &nbsp; Add Item to {item.name}
                </button>
              </div>
            </div>
          </div>
        </div>
        {displayQueue.length > 0 && (
          <table className="table table-condensed">
            <thead>
              <tr>
                <th style={{ width: 30 }} />
                <th style={{ width: 140 }}>Scanned Text</th>
                <th style={{ width: 140 }}>Status</th>
                <th>Errors</th>
                <th>Scanned At</th>
              </tr>
            </thead>
            <tbody>
              {displayQueue.map((item) => (
                <tr>
                  <td>
                    {item.status == "pending" && (
                      <span className="fa fa-spinner fa-spin fa-spinner" />
                    )}
                    {item.status == "error" && (
                      <span className="fa fa-spinner fa-exclamation" />
                    )}
                  </td>
                  <td>{item.reference}</td>
                  <td>{item.status}</td>
                  <td>{item.error}</td>
                  <td>{item.created_at.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <p style={{ marginTop: 20 }}>
          <a
            className="btn btn-danger btn-xs"
            onClick={removeSelectedItems}
            style={{ marginRight: 20 }}
            disabled={selectedItemIds.length == 0}
          >
            Remove Selected
          </a>
          <a
            className="btn btn-primary btn-xs"
            style={{ marginRight: 20 }}
            disabled={selectedItemIds.length == 0}
            onClick={() => showAliquotPrinting(!aliquotPrinting)}
          >
            Print Aliquot Barcodes
          </a>
        </p>
        <div style={{ paddingTop: 10, paddingBottom: 10, textAlign: "center" }}>
          <label style={{ marginRight: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showSampleTechniques}
              onChange={(e) => {
                setShowSampleTechniques(e.target.checked);
              }}
            />
            &nbsp; Show techniques? &nbsp;&nbsp;
          </label>
          <label style={{ marginRight: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showDueDates}
              onChange={(e) => {
                setShowDueDates(e.target.checked);
              }}
            />
            &nbsp; Show due dates/turnaround? &nbsp;&nbsp;
          </label>
          <label style={{ marginRight: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showRequestedTestDate}
              onChange={(e) => {
                setShowRequestedTestDate(e.target.checked);
              }}
            />
            &nbsp; Show requested test date? &nbsp;&nbsp;
          </label>
          <label style={{ marginRight: 10, cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={showItemCounts}
              onChange={(e) => {
                setShowItemCounts(e.target.checked);
              }}
            />
            &nbsp; Show item counts? &nbsp;&nbsp;
          </label>
          {queue.current.queue.length > 0 && (
            <label style={{ cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={showAllScans}
                onChange={(e) => {
                  setShowAllScans(e.target.checked);
                }}
              />
              &nbsp; Show all scans? &nbsp;&nbsp;&nbsp;
            </label>
          )}
        </div>

        {aliquotPrinting && <PrintAliquotBarcodes itemIds={selectedItemIds} />}

        <table className="table table-very-condensed table-hover">
          <thead>
            <tr>
              <th style={{ width: 30 }}>
                {!item.can_only_remove_one_item_at_a_time && (
                  <input type="checkbox" onChange={selectAll} checked={false} />
                )}
              </th>
              <th>Name</th>
              <th>Added</th>
              {showItemCounts && (
                <Fragment>
                  <th>Aliquots</th>
                  <th>Samples</th>
                  <th>Items</th>
                </Fragment>
              )}
              {showDueDates && (
                <Fragment>
                  <th>
                    <span
                      href="#"
                      onClick={(e) => {
                        setSortByDueDate(!sortByDueDate);
                      }}
                      style={{ cursor: "pointer", color: "blue", userSelect: "none" }}
                    >
                      {sortByDueDate && <i className="fa fa-sort-down">&nbsp;</i>}
                      Due Date
                    </span>
                  </th>
                  <th>
                    <span
                      href="#"
                      onClick={(e) => {
                        setSortByTurnaround(!sortByTurnaround);
                      }}
                      style={{ cursor: "pointer", color: "blue", userSelect: "none" }}
                    >
                      {sortByTurnaround && <i className="fa fa-sort-down">&nbsp;</i>}
                      Turnaround
                    </span>
                  </th>
                </Fragment>
              )}
              {showRequestedTestDate && (
                <th>
                  <span
                    href="#"
                    onClick={(e) => {
                      setSortByRequestedTestDate(!sortByRequestedTestDate);
                    }}
                    style={{ cursor: "pointer", color: "blue", userSelect: "none" }}
                  >
                    {sortByRequestedTestDate && <i className="fa fa-sort-down">&nbsp;</i>}
                    Requested Test Date
                  </span>
                </th>
              )}
              {showSampleTechniques && <th style={{ width: 230 }}>Techniques</th>}
            </tr>
          </thead>
          <tbody>
            {children.map((i) => (
              <tr key={i.id}>
                <td>
                  <input type="checkbox" checked={isSelected(i)} onChange={() => select(i)} />
                </td>
                <td>
                  <a href={"/tracking/items/" + i.id}>{i.name}</a>
                </td>
                <td>{i.added_f}</td>
                {showItemCounts &&
                  i.counts && (
                    <Fragment>
                      <td>{showNumber(i.counts.aliquot)}</td>
                      <td>{showNumber(i.counts.sample)}</td>
                      <td>{showNumber(i.counts.total)}</td>
                    </Fragment>
                  )}
                {showDueDates && <td>{i.due_date}</td>}
                {showDueDates && <td>{i.turnaround}</td>}
                {showRequestedTestDate && <td>{i.requested_test_date}</td>}
                {showSampleTechniques && <td>{i.sample_techniques}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
